import React, {useEffect, useMemo, useRef, useState} from 'react';
import Styled from 'styled-components';
import {
	CollapsibleCard,
	FlexColumn,
	FlexRow,
	Heading,
	IconTooltip,
	SegmentedControl,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import {Checkbox} from '@/web-components';
import Util from '../../../../shared/util/util';
import UpdateProjectMutation from '../../../../../mutations/update_project_mutation.modern';
import useIsMount from '../../../../shared/hooks/useIsMount';
import {useRemappingFormatMessage} from '../../../../shared/util/FinancialInternationalisationUtil';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {TooltipFormulaRenderer} from '../../../../../components/TooltipFormulaRenderer';

const ValueCalculationsWrapper = Styled.div`
    ul {
    	margin: 0;
    }
`;

export enum ValueSource {
	TASK = 'TASK',
	ALLOCATION = 'ALLOCATION',
}

export enum Variant {
	PLANNED = 'planned',
	FORECAST = 'forecast',
	ACTUAL = 'actual',
}

const allTheSame = (items: ValueSource[]) => {
	if (items.every(e => e === ValueSource.ALLOCATION)) {
		return ValueSource.ALLOCATION;
	} else if (items.every(e => e === ValueSource.TASK)) {
		return ValueSource.TASK;
	}
	return '';
};

const CheckboxWrapper = Styled.div`
    &[aria-disabled='false'] {
    	cursor: pointer;
    }
  
    display: flex;
    align-items: center;
	gap: 24px;
	padding: 8px;
`;

// From relay generated ValueCalculations_project.graphql.js, (currently not importable).
type ValueCalculations_project = {
	id: string;
	financialSourceSettings?: {
		plannedHours?: string;
		plannedRevenue?: string;
		plannedCost?: string;
		forecastHours?: string;
		forecastRevenue?: string;
		forecastCost?: string;
		actualHours?: string;
		actualRevenue?: string;
		actualCost?: string;
	};
};

type ValueCalculationsProps = {
	project: ValueCalculations_project;
	variant: Variant;
};

type Settings = ValueCalculations_project['financialSourceSettings'];

function toValueSource(value): ValueSource {
	return value ? ValueSource[value] : ValueSource.TASK;
}

function getHours(settings: Settings, variant: Variant): ValueSource {
	let value;
	if (variant === Variant.PLANNED) {
		value = settings?.plannedHours;
	} else if (variant === Variant.FORECAST) {
		value = settings?.forecastHours;
	} else if (variant === Variant.ACTUAL) {
		value = settings?.actualHours;
	}
	return toValueSource(value);
}

function getRevenue(settings: Settings, variant: Variant): ValueSource {
	let value;
	if (variant === Variant.PLANNED) {
		value = settings?.plannedRevenue;
	} else if (variant === Variant.FORECAST) {
		value = settings?.forecastRevenue;
	} else if (variant === Variant.ACTUAL) {
		value = settings?.actualRevenue;
	}
	return toValueSource(value);
}

function getCost(settings: Settings, variant: Variant): ValueSource {
	let value;
	if (variant === Variant.PLANNED) {
		value = settings?.plannedCost;
	} else if (variant === Variant.FORECAST) {
		value = settings?.forecastCost;
	} else if (variant === Variant.ACTUAL) {
		value = settings?.actualCost;
	}
	return toValueSource(value);
}

const ValueCalculationsComponent = ({project, variant}: ValueCalculationsProps): JSX.Element => {
	//const {formatMessage} = useIntl();
	const formatMessage = useRemappingFormatMessage();
	const settings = project.financialSourceSettings;
	const isMount = useIsMount();
	const [hours, setHours] = useState(getHours(settings, variant));
	const [revenue, setRevenue] = useState(getRevenue(settings, variant));
	const [cost, setCost] = useState(getCost(settings, variant));
	const allSame = useMemo(() => allTheSame([hours, revenue, cost]), [hours, revenue, cost]);
	const [separately, setSeparately] = useState(!allSame);

	const prevHours = useRef(hours);
	const prevRevenue = useRef(revenue);
	const prevCost = useRef(cost);

	const changeAll = value => {
		setHours(value);
		setRevenue(value);
		setCost(value);
	};

	const translateTrackingValue = (value: ValueSource) =>
		variant === Variant.ACTUAL && value === ValueSource.TASK ? 'TIME_REG' : value;
	const trackValueCalculationsSettings = (quantity: string, oldValue: ValueSource, newValue: ValueSource) =>
		trackEvent('Value Calculations Settings', 'Changed', {
			pageName: 'Project Settings - Financials',
			category: variant,
			quantity,
			oldValue: translateTrackingValue(oldValue),
			newValue: translateTrackingValue(newValue),
		});

	useEffect(() => {
		if (!isMount) {
			let fields;
			if (variant === Variant.PLANNED) {
				fields = {
					plannedHours: hours,
					plannedRevenue: revenue,
					plannedCost: cost,
				};
			} else if (variant === Variant.FORECAST) {
				fields = {
					forecastHours: hours,
					forecastRevenue: revenue,
					forecastCost: cost,
				};
			} else if (variant === Variant.ACTUAL) {
				fields = {
					actualHours: hours,
					actualRevenue: revenue,
					actualCost: cost,
				};
			}
			Util.CommitMutation(UpdateProjectMutation, {
				project: {id: project.id},
				financialSourceSettings: fields,
			});

			if (prevHours.current !== hours) {
				trackValueCalculationsSettings('Hours', prevHours.current, hours);
			}
			if (prevRevenue.current !== revenue) {
				trackValueCalculationsSettings('Revenue', prevRevenue.current, revenue);
			}
			if (prevCost.current !== cost) {
				trackValueCalculationsSettings('Cost', prevCost.current, cost);
			}

			prevHours.current = hours;
			prevRevenue.current = revenue;
			prevCost.current = cost;
		}
	}, [hours, revenue, cost]);

	const onCheckboxToggle = () => (!separately || allSame) && setSeparately(!separately);

	const taskCalculationsLabelId =
		variant === Variant.ACTUAL
			? 'project_settings.financials.value_calculations.time_registrations'
			: 'project_settings.financials.value_calculations.task_estimates';
	const allocationCalculationsLabelId = 'project_settings.financials.value_calculations.allocations';
	return (
		<ValueCalculationsWrapper>
			<CollapsibleCard open={separately}>
				<CollapsibleCard.Header>
					<FlexColumn gap={'s'}>
						<Heading size={4}>
							{formatMessage({id: 'project_settings.financials.value_calculations.' + variant + '_values'})}
						</Heading>
						<Text color={'medium'}>
							{formatMessage(
								{id: 'project_settings.financials.value_calculations.' + variant + '_values_description'},
								{
									ul: (...msg) => <ul>{msg}</ul>,
									li: (...msg) => <li>{msg}</li>,
								}
							)}
						</Text>
						<FlexRow gap={'s'}>
							<SubHeading>
								{formatMessage({id: 'project_settings.financials.value_calculations.' + variant + '_label'})}
							</SubHeading>
							<IconTooltip>
								<FlexColumn gap={'s'}>
									<SubHeading>
										{formatMessage({
											id: 'project_settings.financials.value_calculations.' + variant + '_tooltip_header',
										})}
									</SubHeading>
									<Text>
										{formatMessage({
											id: 'project_settings.financials.value_calculations.' + variant + '_tooltip',
										})}
									</Text>
								</FlexColumn>
							</IconTooltip>
						</FlexRow>
						<SegmentedControl
							value={allSame}
							disabled={separately}
							onValueChange={value => value && changeAll(value)}
						>
							<SegmentedControl.Item value={ValueSource.TASK}>
								{formatMessage({id: taskCalculationsLabelId})}
							</SegmentedControl.Item>
							<SegmentedControl.Item value={ValueSource.ALLOCATION}>
								{formatMessage({id: allocationCalculationsLabelId})}
							</SegmentedControl.Item>
						</SegmentedControl>
						<CollapsibleCard.Trigger>
							<CheckboxWrapper onClick={onCheckboxToggle} aria-disabled={!allSame}>
								<Checkbox checked={separately} disabled={!allSame} />
								<Text color={allSame ? 'link' : 'medium'}>
									{formatMessage({
										id: 'project_settings.financials.value_calculations.' + variant + '_separately',
									})}
								</Text>
							</CheckboxWrapper>
						</CollapsibleCard.Trigger>
					</FlexColumn>
				</CollapsibleCard.Header>
				<CollapsibleCard.Content>
					<FlexColumn gap={'xxl'}>
						<div>
							<FlexRow gap={'s'}>
								<SubHeading>
									{formatMessage({
										id: 'project_settings.financials.value_calculations.' + variant + '_hours',
									})}
								</SubHeading>
								<IconTooltip>
									<FlexColumn gap={'s'}>
										<SubHeading>
											{formatMessage({
												id: 'project_settings.financials.value_calculations.' + variant + '_hours',
											})}
										</SubHeading>
										<Text>
											{formatMessage({
												id:
													'project_settings.financials.value_calculations.' +
													variant +
													'_' +
													hours.toLowerCase() +
													'_hours_tooltip',
											})}
										</Text>
									</FlexColumn>
								</IconTooltip>
							</FlexRow>
							<SegmentedControl value={hours} onValueChange={value => value && setHours(value)}>
								<SegmentedControl.Item value={ValueSource.TASK}>
									{formatMessage({id: taskCalculationsLabelId})}
								</SegmentedControl.Item>
								<SegmentedControl.Item value={ValueSource.ALLOCATION}>
									{formatMessage({id: allocationCalculationsLabelId})}
								</SegmentedControl.Item>
							</SegmentedControl>
						</div>
						<div>
							<FlexRow gap={'s'}>
								<SubHeading>
									{formatMessage({
										id: 'project_settings.financials.value_calculations.' + variant + '_revenue',
									})}
								</SubHeading>
								<IconTooltip>
									<TooltipFormulaRenderer
										items={[
											{
												title: formatMessage({
													id:
														'project_settings.financials.value_calculations.' +
														variant +
														'_revenue',
												}),
												description: formatMessage({
													id:
														'project_settings.financials.value_calculations.' +
														variant +
														'_revenue_tooltip_description',
												}),
												details: [
													formatMessage({
														id:
															'project_settings.financials.value_calculations.' +
															variant +
															'_hours',
													}),
													'x',
													formatMessage({id: 'common.rate'}),
												],
											},
										]}
									/>
								</IconTooltip>
							</FlexRow>
							<SegmentedControl value={revenue} onValueChange={value => value && setRevenue(value)}>
								<SegmentedControl.Item value={ValueSource.TASK}>
									{formatMessage({id: taskCalculationsLabelId})}
								</SegmentedControl.Item>
								<SegmentedControl.Item value={ValueSource.ALLOCATION}>
									{formatMessage({id: allocationCalculationsLabelId})}
								</SegmentedControl.Item>
							</SegmentedControl>
						</div>
						<div>
							<FlexRow gap={'s'}>
								<SubHeading>
									{formatMessage({
										id: 'project_settings.financials.value_calculations.' + variant + '_cost',
									})}
								</SubHeading>
								<IconTooltip width={'xl'} whiteSpace={'normal'}>
									<TooltipFormulaRenderer
										items={[
											{
												title: formatMessage({
													id: 'project_settings.financials.value_calculations.' + variant + '_cost',
												}),
												details: [
													formatMessage({
														id:
															'project_settings.financials.value_calculations.' +
															variant +
															'_hours',
													}),
													'x',
													formatMessage({id: 'finance_tooltip_components.internal_hourly_cost'}),
												],
											},
										]}
									/>
								</IconTooltip>
							</FlexRow>
							<SegmentedControl value={cost} onValueChange={value => value && setCost(value)}>
								<SegmentedControl.Item value={ValueSource.TASK}>
									{formatMessage({id: taskCalculationsLabelId})}
								</SegmentedControl.Item>
								<SegmentedControl.Item value={ValueSource.ALLOCATION}>
									{formatMessage({id: allocationCalculationsLabelId})}
								</SegmentedControl.Item>
							</SegmentedControl>
						</div>
					</FlexColumn>
				</CollapsibleCard.Content>
			</CollapsibleCard>
		</ValueCalculationsWrapper>
	);
};

export default ValueCalculationsComponent;
